"use client";

/**
 * fund-cost-impact.tsx
 *
 * One-line cost summary card with three ₹-denominated values:
 *   annual fee on ₹1L  |  savings vs category avg  |  10-yr savings vs Regular
 *
 * Slotted under the expense bars in the Cost Breakdown section. Any null
 * field is hidden; if every field is null the whole card is hidden.
 *
 * Used in: src/app/funds/[slug]/page.tsx (Cost Breakdown section)
 */

import { formatRupees } from "@/lib/utils/format";

export interface FundCostImpactProps {
  expensePerLakhAnnual: number | null;
  expenseSavingVsCategory: number | null;
  directVsRegularTenYearImpact: number | null;
}

interface Item {
  label: string;
  value: string;
  tone: "neutral" | "positive" | "negative";
}

function toneClass(t: Item["tone"]): string {
  if (t === "positive") return "text-cgreen";
  if (t === "negative") return "text-cred";
  return "text-foreground";
}

export function FundCostImpact({
  expensePerLakhAnnual,
  expenseSavingVsCategory,
  directVsRegularTenYearImpact,
}: FundCostImpactProps) {
  const items: Item[] = [];

  if (expensePerLakhAnnual !== null && expensePerLakhAnnual !== undefined) {
    items.push({
      label: "Annual fee on \u20B91L",
      value: formatRupees(expensePerLakhAnnual),
      tone: "neutral",
    });
  }
  if (
    expenseSavingVsCategory !== null &&
    expenseSavingVsCategory !== undefined
  ) {
    items.push({
      label:
        expenseSavingVsCategory >= 0
          ? "Saved vs category avg"
          : "Over category avg",
      value: `${formatRupees(Math.abs(expenseSavingVsCategory))}/yr`,
      tone: expenseSavingVsCategory >= 0 ? "positive" : "negative",
    });
  }
  if (
    directVsRegularTenYearImpact !== null &&
    directVsRegularTenYearImpact !== undefined
  ) {
    items.push({
      label: "10-yr saving vs Regular",
      value: `${formatRupees(directVsRegularTenYearImpact)} on \u20B910L`,
      tone: "positive",
    });
  }

  if (items.length === 0) return null;

  return (
    <div className="bg-surface border border-surface-light/10 rounded-xl p-4">
      <div className="text-muted-foreground text-[10px] uppercase tracking-wide mb-2">
        Cost impact
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {items.map((item) => (
          <div key={item.label}>
            <div className="text-dim text-[11px] mb-0.5">{item.label}</div>
            <div
              className={`text-sm font-bold font-tabular ${toneClass(
                item.tone
              )}`}
            >
              {item.value}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
